import * as angular from 'angular';

import { Log, Pattern, ComponentDefinition } from '../../../models';
import { ICurrentApplicationService } from '../../../services/data';
import { tokenize } from '../../../common/helpers';

class LogsTableStateController implements angular.IComponentController {
    config: any;
    criteria: Pattern<Log>[];
    currentPage: number;
    displayedLogs: Log[];
    filteredLogs: Log[];
    searchString: string;
    searchTitle: string;
    selectedLog: Log;
    toggledDisplayDebug: boolean = false;
    toggledDisplayInfo: boolean = false;
    toggledDisplayWarning: boolean = false;
    toggledDisplayError: boolean = false;
    toggledDisplayFatal: boolean = false;

    readonly isLoading: boolean;
    readonly logs: Log[];
    readonly refresh: () => void;

    private _pageSize: number;

    static $inject = [
        '$scope',
        '$translate',
        'currentApplicationService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _currentApplicationService: ICurrentApplicationService) {
    }

    $onInit() {
        this.criteria = [{
            label: 'Message',
            placeholder: 'Search pattern on message',
            property: 'm',
            model: 'm',
            isMatch: (log, regex) => regex.test(log.message)
        }, {
            label: 'User',
            placeholder: 'Search pattern on user name',
            property: 'u',
            model: 'u',
            isMatch: (log, regex) => regex.test(log.user)
        }, {
            label: 'Severity',
            placeholder: 'Search pattern on severity (e.g. Error, Warning)',
            property: 's',
            model: 's',
            isMatch: (log, regex) => regex.test(log.level)
        }, {
            label: 'Application',
            placeholder: 'Search pattern on application name',
            property: 'a',
            model: 'a',
            isMatch: (log, regex) => regex.test(log.application)
        }, {
            label: 'Log file source (service or webapi)',
            placeholder: 'Search pattern on log file (service or webapi)',
            property: 'lo',
            model: 'lo',
            isMatch: (log, regex) => regex.test(log.source)
        }];
        this._pageSize = 50;
        this.currentPage = 0;
        this._$scope.$watch(() => this.searchString, () => {
            this.searchTitle = this.parseSearchString(this.searchString);
            if (this.refresh) {
                this.refresh();
            }
        });
        this._$scope.$watch(() => this.filteredLogs, newValue => {
            this.displayedLogs = this.paginate(newValue);
        });
        this.config = this._currentApplicationService.getApplicationConfiguration();
        if (this.config?.name) {
            this.searchString = `a:${this.config.name}`;
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['logs']?.currentValue) {
            this.currentPage = 0;
        }
    }

    more() {
        this.currentPage++;
        this.displayedLogs = this.paginate(this.filteredLogs);
    }

    less() {
        this.currentPage--;
        this.displayedLogs = this.paginate(this.filteredLogs);
    }

    setFilter(type: string) {
        if (type === 'debug') {
            this.toggledDisplayDebug = !this.toggledDisplayDebug;
        } else if (type === 'info') {
            this.toggledDisplayInfo = !this.toggledDisplayInfo;
        } else if (type === 'warning') {
            this.toggledDisplayWarning = !this.toggledDisplayWarning;
        } else if (type === 'error') {
            this.toggledDisplayError = !this.toggledDisplayError;
        } else if (type === 'fatal') {
            this.toggledDisplayFatal = !this.toggledDisplayFatal;
        }
        const severities: string[] = [];
        if (this.toggledDisplayDebug) {
            severities.push('Debug');
        }
        if (this.toggledDisplayInfo) {
            severities.push('Info');
        }
        if (this.toggledDisplayWarning) {
            severities.push('Warn');
        }
        if (this.toggledDisplayError) {
            severities.push('Error');
        }
        if (this.toggledDisplayFatal) {
            severities.push('Fatal');
        }
        const tokens = this.searchString ? tokenize(this.searchString) : {};
        tokens['s'] = severities.join('|');
        const stringArray: string[] = [];
        for (const key of Object.keys(tokens)) {
            if (key && tokens[key]) {
                stringArray.push(`${key}:${tokens[key]}`);
            }
        }
        this.searchString = stringArray.join(' ');
        if (this.refresh) {
            this.refresh();
        }
    }

    private parseSearchString(searchString: string): string {
        let parsedString = this._$translate.instant('Displaying all logs');
        const tokens = !!searchString ? tokenize(searchString) : {};
        const regexp = new RegExp(this.escapeRegExp('|'), 'g');

        const parsers = [
            { token: 'a', label: 'for application(s) _param_', convertRegex: true },
            { token: 's', label: 'with a severity of _param_', convertRegex: true },
            { token: 'lo', label: 'from _param_ log files', convertRegex: true },
            { token: 'm', label: 'including _param_ in messages', convertRegex: false },
            { token: 'u', label: 'from user _param_', convertRegex: true },
        ];
        const orTranslate = this._$translate.instant('or');
        const parsedStrings = parsers
            .filter(p => p.token in tokens)
            .map(p => {
                let param = tokens[p.token];
                if (p.convertRegex) {
                    param = param.replace(regexp, ` ${orTranslate} `);
                }
                return this._$translate.instant(p.label, { param });
            });
        parsedString = [parsedString]
            .concat(parsedStrings)
            .join(' ');
        return parsedString;
    }

    private escapeRegExp(value: string) {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private paginate(logs: Log[]) {
        return logs ? logs.slice(0, (this.currentPage + 1) * this._pageSize) : [];
    }
}

export const wtLogsTableState: ComponentDefinition = {
    componentId: 'wtLogsTableState',
    controller: LogsTableStateController,
    controllerAs: 'vm',
    template: require('./logsTableState.component.html'),
    bindings: {
        logs: '<',
        isLoading: '<',
        refresh: '&?'
    }
};
